<!-- eslint-disable vue/no-unused-vars -->
<template>
<LoadError v-if="loadError && !isLoading" :message="loadError" @retry="refreshData" />
<template v-else>

  <div class="login-container">
    <!-- Parallax background with overlay as true page background -->
    <v-parallax 
      height="100vh" 
      src="https://wallpaperaccess.com/full/773433.jpg" 
      class="parallax-bg true-bg"
      style="z-index: 0;"
    >
      <div class="overlay-gradient"></div>
    </v-parallax>

    <!-- Centered login card -->
    <div class="login-wrapper">
      <v-card 
        class="login-card glass-card" 
        :class="{ 'mobile-card': isMobile, 'desktop-card': !isMobile }"
        elevation="24"
      >
        <!-- Logo and header section -->
        <div class="card-header">
          <div class="logo-section">
            <div class="logo-container">
              <img :src="Logo" alt="WildTrackPro Logo" class="app-logo" />
            </div>
            <h1 class="app-title">WildTrackPro</h1>
            <div v-if="loading" class="loading-indicator">
              <v-progress-circular size="20" width="2" color="green" indeterminate class="mr-2"></v-progress-circular>
              <span class="loading-text">Signing in...</span>
            </div>
          </div>
        </div>

        <!-- Main content -->
        <v-card-text class="card-content">
          <v-form v-if="!isLoading" class="login-form">
            
            <!-- Login form -->
            <div v-if="!isCredsCorrect" class="login-section">
              <div class="welcome-section">
                <p v-if="!loading && !error" class="welcome-text">Welcome back! Please sign in to continue.</p>
                <v-alert v-if="error" 
                  type="error" 
                  variant="tonal" 
                  class="error-alert mb-6"
                  :text="`${error}. Please speak with a moderator.`">
                </v-alert>
              </div>

              <div class="form-inputs">
                <v-text-field 
                  @keydown.enter="submit" 
                  :loading="loading" 
                  v-model="username"
                  label="Username"
                  variant="outlined"
                  prepend-inner-icon="mdi-account"
                  class="mb-4 modern-input"
                  density="comfortable">
                </v-text-field>
                
                <v-text-field 
                  @keydown.enter="submit" 
                  :loading="loading" 
                  v-model="password" 
                  label="Password"
                  variant="outlined"
                  prepend-inner-icon="mdi-lock"
                  :append-inner-icon="show ? 'mdi-eye' : 'mdi-eye-off'" 
                  :rules="[rules.required]" 
                  :type="show ? 'text' : 'password'"
                  @click:append-inner="show = !show" 
                  class="mb-6 modern-input"
                  density="comfortable">
                </v-text-field>
              </div>

              <div class="action-buttons">
                <v-btn 
                  :loading="loggingIn" 
                  @click="submit" 
                  block
                  size="large"
                  color="green"
                  variant="flat"
                  class="login-btn mb-4"
                  :class="{ 'btn-shake': error }">
                  <v-icon left class="mr-2">mdi-login</v-icon>
                  Sign In
                </v-btn>

                <v-dialog v-model="dialog">
                  <template v-slot:activator="{ props }">
                    <v-btn 
                      v-bind="props" 
                      block
                      size="large"
                      variant="outlined" 
                      color="green"
                      class="signup-btn mb-4">
                      <v-icon left class="mr-2">mdi-account-plus</v-icon>
                      Create Account
                    </v-btn>
                  </template>
                  <SignUp @close="dialog = false" @success="signupSuccess = true" :usernames="usernames" :emails="emails"
                    :isMobile="isMobile" />
                </v-dialog>

                <div class="forgot-links">
                  <v-btn 
                    variant="text" 
                    color="green"
                    class="forgot-btn"
                    @click="forgotUsernameModal = !forgotUsernameModal">
                    <v-icon left size="16" class="mr-1">mdi-help-circle</v-icon>
                    Forgot Username?
                  </v-btn>
                  
                  <v-dialog v-model="dialog_forgot">
                    <template v-slot:activator="{ props }">
                      <v-btn 
                        v-bind="props" 
                        variant="text"
                        color="green"
                        class="forgot-btn">
                        <v-icon left size="16" class="mr-1">mdi-key-variant</v-icon>
                        Forgot Password?
                      </v-btn>
                    </template>
                    <ForgotPassword @close="dialog_forgot = false" @success="reset" />
                  </v-dialog>
                </div>
              </div>

              <!-- Modals -->
              <v-snackbar v-model="signupSuccess" color="success" timeout="3000" class="success-snackbar">
                <v-icon left class="mr-2">mdi-check-circle</v-icon>
                Account created successfully!
              </v-snackbar>
              
              <v-dialog v-model="forgotUsernameModal" max-width="500">
                <v-card class="modern-modal">
                  <v-card-title class="modal-title">
                    <v-icon left class="mr-2">mdi-help-circle</v-icon>
                    Forgot Username
                  </v-card-title>
                  <v-card-text class="modal-text">
                    If you have forgotten your username, please contact us at 
                    <a href="mailto:dean@wildtrackpro.com" class="email-link">dean@wildtrackpro.com</a> 
                    with your phone number and we will get back to you as soon as possible.
                  </v-card-text>
                  <v-card-actions class="modal-actions">
                    <v-spacer></v-spacer>
                    <v-btn color="green" variant="flat" @click="forgotUsernameModal = false">
                      <v-icon left class="mr-1">mdi-check</v-icon>
                      Got it
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>

            <!-- Organisation selection -->
            <div v-else class="org-section">
              <div class="org-header">
                <v-icon size="48" color="green" class="mb-3">mdi-domain</v-icon>
                <h2 class="org-title">Choose Organisation</h2>
                <p class="org-subtitle">Select your organisation to continue</p>
              </div>
              
              <v-combobox 
                v-model="chosenGroup" 
                label="Organisation" 
                :items="orgs"
                variant="outlined"
                prepend-inner-icon="mdi-office-building"
                class="mb-6 modern-input"
                density="comfortable">
              </v-combobox>
              
              <v-btn 
                :loading="loggingIn" 
                :disabled="!chosenGroup" 
                @click="selectGroup" 
                block
                size="large"
                color="green"
                variant="flat"
                class="login-btn">
                <v-icon left class="mr-2">mdi-arrow-right-circle</v-icon>
                Continue to Dashboard
              </v-btn>
            </div>
          </v-form>
          
          <div v-else class="loading-section">
            <Loading />
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>

</template>
</template>

<script>
import { loadData } from '@/services/errors';
import SignUp from '../components/SignUp.vue';
import ForgotPassword from '../components/ForgotPassword.vue';
var SHA256 = require('crypto-js/sha256');
import creds from '@/whatsapp-tracking-361713-97a0a2bb9e20.json';
import Logo from '@/assets/logo-wtp.png';
import { GoogleSpreadsheet } from '@/services/spreadsheet';
export default {
  name: 'LoginComponent',
  setup() {
    return {
      Logo,
    };
  },
  components: {
    SignUp,
    ForgotPassword,
  },
  props: {
    isMobile: null,
  },
  async created() {
    this.refreshData();
  },
  data() {
    return {
      loadError: '',
      username: null,
      isLoading: false,
      usernames: null,
      emails: null,
      signupSuccess: false,
      headers: [],
      password: null,
      error: null,
      loading: false,
      forgotUsernameModal: false,
      show: false,
      dialog: false,
      dialog_forgot: false,
      rownum: null,
      credentials: null,
      userTable: [],
      loggingIn: false,
      rules: {
        required: (value) => !!value || 'Required.',
      },
      orgs: null,
      chosenGroup: null,
      isCredsCorrect: false,
      dbUsername: null,
    };
  },
  methods: {
    selectGroup() {
      this.loggingIn = true;
      this.$emit('success', [this.password, this.dbUsername, this.chosenGroup]);
    },
    async refreshData() {
      return loadData(
        this,
        async () => {
          const org_doc = new GoogleSpreadsheet('1Q_YojT0KtC61QCLIZfTJdonXHG1EWwYMlm5fz9PACA4');
          await org_doc.useServiceAccountAuth({
            client_email: creds.client_email,
            private_key: creds.private_key,
          });
          await org_doc.loadInfo();
          const org_sheet = org_doc.sheetsByTitle['orgs_db'];
          const org_rows = await org_sheet.getRows();
          this.orgs = [...new Set(org_rows.map((o) => o.group_name))];
          this.orgs.sort();
        },
        'isLoading'
      );
    },
    async submit() {
      if (this.loggingIn) return;
      try {
        this.loggingIn = true;
        const doc = new GoogleSpreadsheet('1_QB8JSWR_AINfhpKFLp95NjLP-uFHWd9j1dbNnk2EAk');
        await doc.useServiceAccountAuth({
          client_email: creds.client_email,
          private_key: creds.private_key,
        });
        await doc.loadInfo();
        const sheet = doc.sheetsByTitle['user_db'];
        const rows = await sheet.getRows();
        let password = `A${JSON.stringify(SHA256(this.password).words)}`;
        const normalize = (s) => (s || '').toLowerCase().replace(/\s/g, '');
        let user = rows.filter((r) => {
          return normalize(r.username) === normalize(this.username);
        });
        if (user.length === 0) {
          this.error = 'User not found!';
          this.loggingIn = false;
        } else {
          if (user[0].password === password) {
            if (user[0].groups.split(',')[0] === 'ADMIN') {
              this.$emit('superuser', true);
            } else {
              this.orgs = user[0].groups.split(',');
            }
            this.dbUsername = user[0].username;
            this.isCredsCorrect = true;
          } else {
            this.error = 'Incorrect password. Please try again.';
          }
          this.loggingIn = false;
        }
      } finally {
        this.loggingIn = false;
      }
    },
    async reset(r) {
      try {
        const email = r[0] ? r[0].trim().toLowerCase() : '';
        let newPassword = JSON.stringify(SHA256(r[1]).words);
        newPassword = `A${newPassword}`;
        const doc = new GoogleSpreadsheet('1_QB8JSWR_AINfhpKFLp95NjLP-uFHWd9j1dbNnk2EAk');
        await doc.useServiceAccountAuth({
          client_email: creds.client_email,
          private_key: creds.private_key,
        });
        await doc.loadInfo();
        const sheet = doc.sheetsByTitle['user_db'];
        const rows = await sheet.getRows();

        // Find the user row by email (case-insensitive, trimmed)
        const userRow = rows.find((row) => {
          const rowEmail = row.email ? row.email.trim().toLowerCase() : '';
          return rowEmail === email;
        });
        if (!userRow) {
          console.error('Password reset failed: No user found with email:', email);
          alert('Password reset failed: User not found. Please try again.');
          return;
        }
        userRow.password = newPassword;
        await userRow.save();
        this.dialog_forgot = false;
        alert('Password reset successful! You can now log in with your new password.');
        window.location.reload();
      } catch (error) {
        console.error('Password reset error:', error);
        alert('An error occurred while resetting your password. Please try again.');
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}


/* Parallax as true background */
.parallax-bg.true-bg {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  z-index: 0;
}

.parallax-bg.true-bg .overlay-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(76, 175, 80, 0.3) 30%,
    rgba(46, 125, 50, 0.4) 70%,
    rgba(27, 94, 32, 0.5) 100%
  );
  z-index: 1;
  pointer-events: none;
}

.login-wrapper {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.glass-card {
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 24px !important;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.2),
    0 10px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.glass-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
}

.desktop-card {
  width: 450px;
  max-width: 90vw;
  padding: 40px;
}

.mobile-card {
  width: 95vw;
  padding: 30px 20px;
}

.glass-card:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 15px 30px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.card-header {
  text-align: center;
  margin-bottom: 32px;
}

.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-container {
  margin-bottom: 16px;
}

.app-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
}

.app-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
  margin-top: 8px;
}

.loading-text {
  font-size: 0.9rem;
  opacity: 0.8;
}

.card-content {
  padding: 0 !important;
}

.welcome-section {
  text-align: center;
  margin-bottom: 32px;
}

.welcome-text {
  color: rgba(0, 0, 0, 0.8);
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
}

.error-alert {
  border-radius: 12px !important;
  backdrop-filter: blur(10px);
  background: rgba(244, 67, 54, 0.15) !important;
  border: 1px solid rgba(244, 67, 54, 0.3) !important;
}

.form-inputs {
  margin-bottom: 24px;
}

.modern-input {
  margin-bottom: 20px;
}

.modern-input :deep(.v-field) {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modern-input :deep(.v-field:hover) {
  background: rgba(255, 255, 255, 0.8) !important;
  border-color: rgba(76, 175, 80, 0.5) !important;
}

.modern-input :deep(.v-field--focused) {
  background: rgba(255, 255, 255, 0.9) !important;
  border-color: rgba(76, 175, 80, 0.8) !important;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
}

.modern-input :deep(.v-label) {
  color: rgba(0, 0, 0, 0.6) !important;
  font-weight: 500;
}

.modern-input :deep(.v-input__control .v-field .v-field__input) {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 500;
}

.modern-input :deep(.v-icon) {
  color: rgba(76, 175, 80, 0.7) !important;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-btn {
  background: linear-gradient(135deg, #4CAF50, #2E7D32) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600;
  font-size: 1rem;
  text-transform: none;
  padding: 16px !important;
  box-shadow: 
    0 8px 16px rgba(76, 175, 80, 0.3),
    0 4px 8px rgba(76, 175, 80, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 12px 24px rgba(76, 175, 80, 0.4),
    0 6px 12px rgba(76, 175, 80, 0.3) !important;
}

.login-btn:active {
  transform: translateY(0);
}

.btn-shake {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.signup-btn {
  border: 2px solid rgba(76, 175, 80, 0.6) !important;
  background: rgba(255, 255, 255, 0.3) !important;
  color: rgba(0, 0, 0, 0.8) !important;
  border-radius: 12px !important;
  font-weight: 600;
  font-size: 1rem;
  text-transform: none;
  padding: 16px !important;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.signup-btn:hover {
  background: rgba(255, 255, 255, 0.5) !important;
  border-color: rgba(76, 175, 80, 0.8) !important;
  transform: translateY(-1px);
}

.forgot-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.forgot-btn {
  color: rgba(0, 0, 0, 0.6) !important;
  font-size: 0.9rem;
  text-transform: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.forgot-btn:hover {
  color: rgba(76, 175, 80, 0.9) !important;
  background: rgba(76, 175, 80, 0.1) !important;
}

.org-section {
  text-align: center;
}

.org-header {
  margin-bottom: 32px;
}

.org-title {
  font-size: 2rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 16px 0 8px 0;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
}

.org-subtitle {
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}

.loading-section {
  text-align: center;
  padding: 40px 0;
}

.modern-modal {
  backdrop-filter: blur(20px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.modal-title {
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 600;
  padding: 24px 24px 16px 24px;
}

.modal-text {
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.6;
  padding: 0 24px 16px 24px;
}

.email-link {
  color: #4CAF50;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.email-link:hover {
  color: #2E7D32;
  text-decoration: underline;
}

.modal-actions {
  padding: 16px 24px 24px 24px;
}

/* Animation classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .app-title {
    font-size: 2rem;
  }
  
  .org-title {
    font-size: 1.6rem;
  }
  
  .welcome-text {
    font-size: 1rem;
  }
  
  .desktop-card,
  .mobile-card {
    padding: 24px 16px;
  }
  
  .forgot-links {
    flex-direction: column;
    gap: 12px;
  }
}

/* Success snackbar styling */
:deep(.v-snackbar) {
  border-radius: 12px !important;
}

:deep(.v-snackbar .v-snackbar__wrapper) {
  backdrop-filter: blur(20px);
  background: rgba(76, 175, 80, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.success-snackbar :deep(.v-snackbar__wrapper) {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95), rgba(46, 125, 50, 0.95)) !important;
}

/* Dialog animations */
:deep(.v-dialog) {
  border-radius: 16px !important;
}

:deep(.v-overlay__content) {
  animation: modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Enhanced hover effects */
.glass-card {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover {
  transform: translateY(-3px) scale(1.01);
  box-shadow: 
    0 30px 60px rgba(0, 0, 0, 0.2),
    0 20px 40px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Focus states for accessibility */
.modern-input :deep(.v-field--focused) {
  transform: scale(1.02);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Button press effects */
.login-btn:active,
.signup-btn:active {
  transform: translateY(1px) scale(0.98);
  transition: transform 0.1s ease;
}
</style>